import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Mail,
  XCircle
} from 'lucide-react';
import { AlertRule, AlertRuleFormData, TimeRange } from '../../../types/solarMonitoring';

interface AlertRuleModalProps {
  isOpen: boolean;
  editingRule: AlertRule | null;
  onClose: () => void;
  onSubmit: (formData: AlertRuleFormData) => void;
  onTestEmail: (emails: string[]) => void;
  testEmailLoading: boolean;
}

export const AlertRuleModal: React.FC<AlertRuleModalProps> = ({ 
  isOpen,
  editingRule,
  onClose,
  onSubmit,
  onTestEmail,
  testEmailLoading
}) => {
  const [formData, setFormData] = useState<AlertRuleFormData>({
    name: '',
    alertEmails: [],
    timeRanges: [{
      startTime: '09:00',
      endTime: '17:00',
      thresholdPercent: 80,
      intervalMinutes: 15
    }],
    maxAlertsPerHour: 1,
    isActive: true
  });

  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (editingRule) {
      setFormData({
        name: editingRule.name,
        alertEmails: editingRule.alertEmails,
        timeRanges: editingRule.timeRanges,
        maxAlertsPerHour: editingRule.maxAlertsPerHour,
        isActive: editingRule.isActive
      });
    } else {
      resetForm();
    }
  }, [editingRule]);

  const resetForm = () => {
    setFormData({
      name: '',
      alertEmails: [],
      timeRanges: [{
        startTime: '09:00',
        endTime: '17:00',
        thresholdPercent: 80,
        intervalMinutes: 15
      }],
      maxAlertsPerHour: 1,
      isActive: true
    });
    setNewEmail('');
  };

  const addEmail = () => {
    if (newEmail && !formData.alertEmails.includes(newEmail)) {
      setFormData(prev => ({
        ...prev,
        alertEmails: [...prev.alertEmails, newEmail]
      }));
      setNewEmail('');
    }
  };

  const removeEmail = (email: string) => {
    setFormData(prev => ({
      ...prev,
      alertEmails: prev.alertEmails.filter(e => e !== email)
    }));
  };

  const addTimeRange = () => {
    setFormData(prev => ({
      ...prev,
      timeRanges: [...prev.timeRanges, {
        startTime: '09:00',
        endTime: '17:00',
        thresholdPercent: 80,
        intervalMinutes: 15
      }]
    }));
  };

  const removeTimeRange = (index: number) => {
    setFormData(prev => ({
      ...prev,
      timeRanges: prev.timeRanges.filter((_, i) => i !== index)
    }));
  };

  const updateTimeRange = (index: number, field: keyof TimeRange, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      timeRanges: prev.timeRanges.map((range, i) => 
        i === index ? { ...range, [field]: value } : range
      )
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    resetForm();
  };

  const handleTestEmail = () => {
    onTestEmail(formData.alertEmails);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rule Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rule Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. Peak Hours Low Output Alert"
                required
              />
            </div>

            {/* Email Recipients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alert Recipients
              </label>
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email address"
                  />
                  <button
                    type="button"
                    onClick={addEmail}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.alertEmails.map((email) => (
                    <div
                      key={email}
                      className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => removeEmail(email)}
                        className="hover:text-blue-600"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Time Ranges */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Time Ranges & Thresholds
                </label>
                <button
                  type="button"
                  onClick={addTimeRange}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Range</span>
                </button>
              </div>
              <div className="space-y-3">
                {formData.timeRanges.map((range, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={range.startTime}
                          onChange={(e) => updateTimeRange(index, 'startTime', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">End Time</label>
                        <input
                          type="time"
                          value={range.endTime}
                          onChange={(e) => updateTimeRange(index, 'endTime', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Threshold %</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={range.thresholdPercent}
                          onChange={(e) => updateTimeRange(index, 'thresholdPercent', parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Duration (min)</label>
                        <div className="flex">
                          <input
                            type="number"
                            min="1"
                            value={range.intervalMinutes}
                            onChange={(e) => updateTimeRange(index, 'intervalMinutes', parseInt(e.target.value))}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          {formData.timeRanges.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeTimeRange(index)}
                              className="ml-2 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Max Alerts Per Hour */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Alerts Per Hour
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={formData.maxAlertsPerHour}
                onChange={(e) => setFormData(prev => ({ ...prev, maxAlertsPerHour: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Activate this rule immediately
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={testEmailLoading || formData.alertEmails.length === 0}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 disabled:text-gray-400"
              >
                {testEmailLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                <span>Test Email</span>
              </button>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};